import Link from "next/link";
import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import HeadTermLinks from "@/components/courses/head-term-links";
import PathwaySelectorRequest from "@/components/forms/pathway-selector-request";

const Listing = dynamic(() => import("@/components/listing"));

export type PersonaPageData = {
  eyebrow: string;
  title: string;
  intro: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel: string;
  fitTitle: string;
  fitCopy: string;
  goals: Array<{ title: string; description: string; href: string; label: string }>;
  routes: Array<{ title: string; description: string; note: string; href: string; label: string }>;
  steps: Array<{ stage: string; title: string; description: string }>;
  faqs: Array<{ question: string; answer: string }>;
  related: Array<{ label: string; href: string }>;
  sourcePage: string;
  entryPersona: "beginner" | "career_changer" | "beauty_therapist" | "medical_professional";
};

function LinkButton({ href, children, secondary = false }: { href: string; children: ReactNode; secondary?: boolean }) {
  return (
    <Link
      href={href}
      className={secondary
        ? "inline-flex min-h-11 items-center justify-center rounded-full border border-stone-300 bg-white px-6 py-3 text-sm font-semibold text-stone-800 transition hover:border-brand-400 hover:text-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        : "inline-flex min-h-11 items-center justify-center rounded-full bg-stone-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"}
    >
      {children}
    </Link>
  );
}

export default function PersonaPage({ data, catalogueProps }: { data: PersonaPageData; catalogueProps: any }) {
  return (
    <div>
      <section className="relative overflow-hidden border-b border-stone-200 bg-[radial-gradient(circle_at_85%_10%,rgba(210,170,160,0.24),transparent_28%),linear-gradient(145deg,#fbfaf8_0%,#f1eee8_100%)]">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 sm:py-20 lg:grid-cols-[1.15fr_.85fr] lg:items-center lg:px-8 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-700">{data.eyebrow}</p>
            <h1 className="mt-5 max-w-4xl text-4xl font-black leading-tight tracking-tight text-stone-950 sm:text-5xl">{data.title}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-stone-700">{data.intro}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <LinkButton href={data.primaryHref}>{data.primaryLabel}</LinkButton>
              <LinkButton href="#training-options" secondary>{data.secondaryLabel}</LinkButton>
            </div>
          </div>
          <aside className="rounded-[2rem] border border-white/80 bg-white/75 p-7 shadow-[0_24px_70px_rgba(41,37,36,0.09)] backdrop-blur sm:p-9">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-700">A useful starting point</p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-stone-900">{data.fitTitle}</h2>
            <p className="mt-4 text-base leading-7 text-stone-600">{data.fitCopy}</p>
            <Link href="/which-aesthetics-course-is-right-for-me/#choose-your-start" className="mt-6 inline-flex min-h-11 items-center font-semibold text-brand-700 underline decoration-brand-300 underline-offset-4 hover:text-brand-900">
              Explore all starting points
            </Link>
          </aside>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-700">Start with your goal</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-stone-950 sm:text-4xl">Different goals can call for different training</h2>
            <p className="mt-4 text-base leading-7 text-stone-600">A treatment interest, qualification and delivery preference are separate parts of the decision. Course entry requirements and assessments vary.</p>
          </div>
          <div className="mt-9 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {data.goals.map((goal) => (
              <article key={goal.title} className="flex flex-col rounded-3xl border border-stone-200 bg-[#fbfaf8] p-6">
                <h3 className="text-lg font-bold text-stone-900">{goal.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-6 text-stone-600">{goal.description}</p>
                <Link className="mt-5 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-brand-700 underline decoration-brand-300 underline-offset-4 hover:text-brand-900" href={goal.href}>{goal.label} <span aria-hidden="true">→</span></Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="training-options" className="scroll-mt-24 bg-[#f4f1eb] py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-700">Compare route types</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-stone-950 sm:text-4xl">Training options to review</h2>
            <p className="mt-4 text-base leading-7 text-stone-600">These route descriptions help you compare what to look at. They do not confirm entry or make an admissions decision. Check the current course details and ask admissions to review your background where needed.</p>
          </div>
          <div className="mt-9 grid gap-5 lg:grid-cols-3">
            {data.routes.map((route, index) => (
              <article key={route.title} className="rounded-3xl border border-stone-200 bg-white p-7 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-600">Route {index + 1}</p>
                <h3 className="mt-3 text-xl font-bold text-stone-900">{route.title}</h3>
                <p className="mt-3 text-sm leading-6 text-stone-600">{route.description}</p>
                <p className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-sm leading-6 text-stone-700">{route.note}</p>
                <Link href={route.href} className="mt-5 inline-flex min-h-11 items-center text-sm font-semibold text-brand-700 underline decoration-brand-300 underline-offset-4 hover:text-brand-900">{route.label} <span className="ml-2" aria-hidden="true">→</span></Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="courses-listing" aria-label="Current courses" className="scroll-mt-24 bg-white py-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="mb-7 text-sm leading-6 text-stone-600">These are current course records. Review each course for its full entry criteria, study and assessment commitments, fees and published dates. If a detail is not shown, contact admissions for current information.</p>
        </div>
        <Listing {...catalogueProps} />
      </section>

      <section className="bg-stone-950 py-16 text-white sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-300">A considered next step</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">How a training route takes shape</h2>
          </div>
          <ol className="mt-9 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {data.steps.map((step, index) => (
              <li key={step.title} className="rounded-3xl border border-white/15 bg-white/[0.06] p-6">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-300">{step.stage}</span>
                <h3 className="mt-3 text-lg font-bold">{step.title}</h3>
                <p className="mt-3 text-sm leading-6 text-stone-300">{step.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-[.75fr_1.25fr] lg:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-700">Common questions</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-stone-950">Before you choose</h2>
          </div>
          <div className="space-y-4">
            {data.faqs.map((faq) => (
              <details key={faq.question} className="group rounded-2xl border border-stone-200 bg-[#fbfaf8] p-5">
                <summary className="min-h-11 cursor-pointer py-2 font-semibold text-stone-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600">{faq.question}</summary>
                <p className="pb-2 pt-3 text-sm leading-6 text-stone-600">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[linear-gradient(180deg,#f4f1eb_0%,#fff_100%)] py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <PathwaySelectorRequest
            sourcePage={data.sourcePage}
            entryPersona={data.entryPersona}
            placement="persona_page_inline"
            heading="Ask admissions to review your options"
            description="Share your background and what you hope to learn. The admissions team can explain relevant courses and confirm which entry requirements apply. A suggested route is not an admission decision."
          />
        </div>
      </section>

      <section className="bg-white py-10">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <HeadTermLinks variant="hub" />
        </div>
      </section>

      <nav aria-label="Related training routes" className="border-t border-stone-200 bg-white py-10">
        <div className="mx-auto flex max-w-7xl flex-wrap gap-x-7 gap-y-3 px-6 lg:px-8">
          {data.related.map((item) => <Link className="min-h-11 inline-flex items-center text-sm font-semibold text-stone-700 underline decoration-stone-300 underline-offset-4 hover:text-brand-700" href={item.href} key={item.href}>{item.label}</Link>)}
        </div>
      </nav>
    </div>
  );
}
